import { useState, useRef, useEffect } from 'react'
import { Formik, ErrorMessage, Field } from 'formik';
import * as Yup from "yup";
import { useParams, useNavigate } from 'react-router-dom';
import GenreChooser from "./Components/GenreChooser";
import TagChooser from "./Components/TagChooser";
import DirectorAutocomplete from './Components/DirectorAutocomplete';
import ModalLoading from './Components/ModalLoading';
import { ErrorBanner } from './Components/Banners';
import { useFirestore, useAuth } from 'reactfire';
import { getApp } from "firebase/app";
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, deleteObject } from "firebase/storage"; // 2026-09-01: ref/uploadBytes now actually used -- poster replace-on-save (see handleSubmitInner); deleteObject added 2026-09-03 for old-poster cleanup
import Resizer from "react-image-file-resizer";
import useTitle from "./Hooks/useTitle";
import { formatYear } from './Functions/dates.js';
import cloneDeep from 'lodash/cloneDeep';
import { PlusIcon } from '@heroicons/react/20/solid'
import AddProducerModal from './Components/AddProducerModal';
import AddActorModal from './Components/AddActorModal';
import AddWatchModal from './Components/AddWatchModal';
import CountryChooser from './Components/CountryChooser';
import LanguageChooser from './Components/LanguageChooser';
import { extractYoutubeId, extractTmdbPath, extractImdbId } from './Functions/urlParsing.js';
import { fetchTmdbMovieDetails } from './Functions/tmdbService.js';
import { findOrCreatePeople } from './Functions/personLookup.js';

// https://firebase.google.com/docs/storage/web/start re multiple buckets
// https://stackoverflow.com/questions/56149756/reactjs-how-to-handle-image-file-upload-with-formik

export default function TitleEditPage({ authUser }) {
    // STATE

    // eslint-disable-next-line
    const [debugShowFormErrors, setDebugShowFormErrors] = useState(true);

    // eslint-disable-next-line
    const [artworkImgEncoded, setArtworkImgEncoded] = useState(undefined);

    // eslint-disable-next-line
    const [artworkImgFile, setArtworkImgFile] = useState(undefined);
    const [modalProducerOpen, setModalProducerOpen] = useState(false);
    const [modalActorOpen, setModalActorOpen] = useState(false);
    const [modalWatchOpen, setModalWatchOpen] = useState(false);
    const [modalSpinnerOpen, setModalSpinnerOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [modalMessage, setModalMessage] = useState(undefined);

    // HOOKS
    const { titleId } = useParams();
    const title = useTitle(titleId); // ready to use JS Object
    const firebaseApp = getApp();
    // eslint-disable-next-line
    const storage = getStorage(firebaseApp, "gs://findflix-cdn");
    const auth = useAuth();
    const firestore = useFirestore();
    const navigate = useNavigate();
    const artworkImgRef = useRef()
    const ratingFieldRef = useRef();
    const formikRef = useRef();
    // 2026-08-19: refs for the uncontrolled inputs the TMDB re-fetch can update --
    // Formik's setFieldValue alone doesn't repaint a defaultValue-based input.
    const titleDisplayRef = useRef();
    const synopsisRef = useRef();
    const releasedRef = useRef();
    const runtimeHHRef = useRef();
    const runtimeMMRef = useRef();
    const idIMDBRef = useRef();
    const [tmdbFetchStatus, setTmdbFetchStatus] = useState(undefined);
    const [tmdbFetchNote, setTmdbFetchNote] = useState(undefined);

    // munge firestore structure into state array of simple objects
    // const flattenedArray = [];
    // for (const guid in (title.producers) ? title.producers : {}) {
    //     for (const titleX in title.producers[guid]) {
    //         console.log(JSON.stringify(title.producers[guid]));
    //         flattenedArray.push({ guid: guid, title: titleX, name: title.producers[guid][titleX] });
    //     }
    // }

    const [selectedDirectors, setSelectedDirectors] = useState((title.directors) ? Object.entries(title.directors).map(([guid, name]) => ({ guid, name })) : []);
    const [selectedWriters, setSelectedWriters] = useState((title.writers) ? Object.entries(title.writers).map(([guid, name]) => ({ guid, name })) : []);
    const [selectedCinematographers, setSelectedCinematographers] = useState((title.cinematographers) ? Object.entries(title.cinematographers).map(([guid, name]) => ({ guid, name })) : []);
    const [selectedEditors, setSelectedEditors] = useState((title.editors) ? Object.entries(title.editors).map(([guid, name]) => ({ guid, name })) : []);
    const [selectedComposers, setSelectedComposers] = useState((title.composers) ? Object.entries(title.composers).map(([guid, name]) => ({ guid, name })) : []);
    const [selectedShowrunners, setSelectedShowrunners] = useState((title.showrunners) ? title.showrunners : []);
    const [selectedCreators, setSelectedCreators] = useState((title.creators) ? title.creators : []);
    const [selectedProducers, setSelectedProducers] = useState((title.producers) ? title.producers : []);
    const [selectedCast, setSelectedCast] = useState((title.cast) ? title.cast : []);
    const [selectedWatch, setSelectedWatch] = useState((title.watch) ? title.watch : []);
    const [useSeriesMetadata, setUseSeriesMetadata] = useState((title.kind && (title.kind === 2 || title.kind === "2")) ? true : false);

    // FORM
    const initialValues = {
        titleDisplay: title.title,
        genres: (title.genres) ? title.genres : [],
        tags: (title.tags) ? title.tags : [],
        synopsis: title.synopsis,
        kind: title.kind,
        released: (title.release_date) ? formatYear(title.release_date.toDate()) : undefined,
        runtimeHH: title.runtimeHH,
        runtimeMM: title.runtimeMM,
        runtime: title.runtime,
        rating: title.rating,
        trailer: title.trailer,
        featured: (title.featured) ? title.featured : false,
        directorGuid: (title.directors) ? Object.keys(title.directors)[0] : undefined,
        directorName: (title.directors) ? Object.values(title.directors)[0] : undefined,
        idIMDB: (title.ids && title.ids.imdb) ? title.ids.imdb : undefined,
        idJW: (title.ids && title.ids.jw) ? title.ids.jw : undefined,
        idRT: (title.ids && title.ids.rt) ? title.ids.rt : undefined,
        idTMDB: (title.ids && title.ids.tmdb) ? title.ids.tmdb : undefined,
        idWiki: (title.ids && title.ids.wiki) ? title.ids.wiki : undefined,
        country: (title.country && title.country.length > 0) ? title.country[0] : undefined,
        language: title.language,
        numEpisodes: (title.numEpisodes !== null && title.numEpisodes !== undefined) ? title.numEpisodes : undefined,
        // 2026-08-18: channels defaults to BlackPhlix-only if the doc somehow has no
        // channels set at all, matching the pre-migration default every title used to get.
        channelBlackphlix: (title.channels !== undefined) ? title.channels.includes(1) : true,
        channelFindflix: (title.channels !== undefined) ? title.channels.includes(2) : false,
    };
    const validationSchema = Yup.object({
        titleDisplay: Yup.string("Enter title").required("Required"),
        synopsis: Yup.string("Enter synopsis").required("Required"),
        trailer: Yup.string("Enter trailer").required("Required").matches(/^[a-zA-Z0-9_-]{11}$/, "Invalid YouTube video ID"),
        directorGuid: Yup.string("Director ID").optional(),
        directorName: Yup.string("Director name").optional(),
        genres: Yup.array().min(1, "Choose at least 1").required("Required"),
        tags: Yup.array().optional(),
        kind: Yup.string().oneOf(["1", "2"], "Must agree to something").required(),
        // 2026-08-19: {excludeEmptyString: true} on every optional+matches pairing, and
        // tt\d{7,} (not {7}) for IMDb -- same two validation bugs fixed on the Add page
        // the same day (empty string fails .matches() even on .optional() fields, and
        // IMDb ids issued since ~2019 have 8+ digits). Without these, Save silently
        // stays disabled on affected titles.
        released: Yup.string("Enter year of release").optional().matches(/^(19|20)\d{2}$/, { message: "Invalid year", excludeEmptyString: true }),
        runtime: Yup.string("Enter runtime").optional().matches(/^[0-9hmHM ]{1,20}$/, { message: "Invalid format", excludeEmptyString: true }),
        runtimeHH: Yup.number("Enter runtime HH").optional().min(0).max(23).typeError('HH must be a number'),
        runtimeMM: Yup.number("Enter runtime MM").optional().min(0).max(59).typeError('MM must be a number'),
        rating: Yup.string("Enter rating").optional().matches(/^[0-9A-Za-z\-+ ]{1,20}$/, { message: "Invalid characters", excludeEmptyString: true }),
        idIMDB: Yup.string("Enter IMDB").optional().matches(/^tt\d{7,}$/, { message: "Invalid ID", excludeEmptyString: true }),
        idJW: Yup.string("Enter Just Watch").optional().matches(/^\//, { message: "Invalid ID", excludeEmptyString: true }),
        idRT: Yup.string("Enter Rotten Tomatoes").optional().matches(/^\//, { message: "Invalid ID", excludeEmptyString: true }),
        idTMDB: Yup.string("Enter TMDB").optional().matches(/^\//, { message: "Invalid ID", excludeEmptyString: true }),
        idWiki: Yup.string("Enter Wikipedia").optional(),
        featured: Yup.boolean().required(),
        country: Yup.string("Enter country").optional().matches(/^[a-zA-Z]{2}$/, { message: "Invalid country", excludeEmptyString: true }),
        language: Yup.string("Enter language").optional().matches(/^[a-zA-Z]{2}$/, { message: "Invalid language", excludeEmptyString: true }),
        numEpisodes: Yup.number("Enter number of episodes").optional().min(0).max(1000).typeError('Number of episodes must be a number'),
        channelBlackphlix: Yup.boolean(),
        channelFindflix: Yup.boolean(),
    }).test('at-least-one-channel', 'Choose at least one channel', (values) =>
        Boolean(values.channelBlackphlix) || Boolean(values.channelFindflix)
    );

    // FORM ACTION

    async function handleSubmit(form) {
        // get ready in the following state
        setErrorMessage(undefined);
        setModalSpinnerOpen(true);

        // 2026-08-29 (URGENT fix, same session): the whole body below is now wrapped
        // in a try/catch. Tonight's actual bug (form.numEpisodes.trim() throwing on a
        // kind:2 title, fixed above) was an uncaught synchronous throw with nothing
        // to catch it and no setModalSpinnerOpen(false) anywhere in this file -- the
        // "Please be patient" modal (only ever closed by navigating away on success)
        // hung forever with zero error shown. This wrapper is a safety net so any
        // OTHER unexpected error in this save path fails visibly (ErrorBanner + modal
        // closes) instead of repeating that same silent-hang failure mode.
        try {
            await handleSubmitInner(form);
        } catch (error) {
            console.error('[TitleEditPage] Save failed:', error);
            setErrorMessage('Something went wrong saving this title -- nothing was lost, but please try again or let Pascal know if it keeps happening.');
            setModalSpinnerOpen(false);
        }
    }

    async function handleSubmitInner(form) {
        var payload = {
            updator: auth.currentUser.uid,
            updated: serverTimestamp(),
            title: form.titleDisplay,
            title_sort: form.titleDisplay,
            synopsis: form.synopsis,
            trailer: form.trailer,
            genres: form.genres,
            tags: form.tags,
            featured: (form.featured === "1" || form.featured === 1 ? true : false),
            kind: parseInt(form.kind),
            channels: [
                ...(form.channelBlackphlix ? [1] : []),
                ...(form.channelFindflix ? [2] : []),
            ]
        };

        // add if valid
        if (form.released !== undefined && parseDateString(form.released) instanceof Date) {
            payload.release_date = parseDateString(form.released);
        }

        // add if valid
        if (form.runtimeHH !== undefined && form.runtimeMM !== undefined && !isNaN(form.runtimeHH) && !isNaN(form.runtimeMM)) {
            payload.runtimeHH = parseInt(form.runtimeHH);
            payload.runtimeMM = parseInt(form.runtimeMM);
        }

        // add if valid
        if (form.rating !== undefined) {
            payload.rating = form.rating;
        }

        // add site IDs if valid
        const siteIds = {};
        // truthy (not just !== undefined): idIMDB has a visible input as of 2026-08-19,
        // so it can legitimately be '' -- don't store an empty id.
        if (form.idIMDB) {
            siteIds['imdb'] = form.idIMDB;
        }
        if (form.idJW !== undefined) {
            siteIds['jw'] = form.idJW;
        }
        if (form.idRT !== undefined) {
            siteIds['rt'] = form.idRT;
        }
        if (form.idTMDB !== undefined) {
            siteIds['tmdb'] = form.idTMDB;
        }
        if (form.idWiki !== undefined) {
            siteIds['wiki'] = form.idWiki;
        }

        if (Object.keys(siteIds).length > 0) {
            payload.ids = siteIds;
        }

        // add Directors if valid
        if (selectedDirectors !== undefined && Array.isArray(selectedDirectors)) {
            var directors = {};
            var directorsIdx = [];

            selectedDirectors.forEach(item => {
                directors[item.guid] = item.name;
                directorsIdx.push(item.guid);
            });

            payload.directors = directors;
            // 2026-08-19: directorsIdx was never being written here (only on Add), leaving
            // it stale after any edit that changed the director list -- fixed while adding
            // the equivalent Writer/etc. fields below.
            payload.directorsIdx = directorsIdx;
        }

        // add Writers if valid
        if (selectedWriters !== undefined && Array.isArray(selectedWriters)) {
            var writers = {};
            var writersIdx = [];

            selectedWriters.forEach(item => {
                writers[item.guid] = item.name;
                writersIdx.push(item.guid);
            });

            payload.writers = writers;
            payload.writersIdx = writersIdx;
        }

        // add Cinematographers if valid
        if (selectedCinematographers !== undefined && Array.isArray(selectedCinematographers)) {
            var cinematographers = {};
            var cinematographersIdx = [];

            selectedCinematographers.forEach(item => {
                cinematographers[item.guid] = item.name;
                cinematographersIdx.push(item.guid);
            });

            payload.cinematographers = cinematographers;
            payload.cinematographersIdx = cinematographersIdx;
        }

        // add Editors if valid
        if (selectedEditors !== undefined && Array.isArray(selectedEditors)) {
            var editors = {};
            var editorsIdx = [];

            selectedEditors.forEach(item => {
                editors[item.guid] = item.name;
                editorsIdx.push(item.guid);
            });

            payload.editors = editors;
            payload.editorsIdx = editorsIdx;
        }

        // add Composers if valid
        if (selectedComposers !== undefined && Array.isArray(selectedComposers)) {
            var composers = {};
            var composersIdx = [];

            selectedComposers.forEach(item => {
                composers[item.guid] = item.name;
                composersIdx.push(item.guid);
            });

            payload.composers = composers;
            payload.composersIdx = composersIdx;
        }

        // add Showrunners if valid
        if (selectedShowrunners !== undefined && Array.isArray(selectedShowrunners)) {
            var showrunners = [];
            var showrunnersIdx = [];

            selectedShowrunners.forEach(item => {
                showrunners.push(item);
                showrunnersIdx.push(item.guid);
            });

            payload.showrunners = showrunners;
            payload.showrunnersIdx = showrunnersIdx;
        }

        // add Creators if valid
        if (selectedCreators !== undefined && Array.isArray(selectedCreators)) {
            var creators = [];
            var creatorsIdx = [];

            selectedCreators.forEach(item => {
                creators.push(item);
                creatorsIdx.push(item.guid);
            });

            payload.creators = creators;
            payload.creatorsIdx = creatorsIdx;
        }

        // add Producers if valid
        if (selectedProducers !== undefined && Array.isArray(selectedProducers)) {
            var producers = [];
            var producersIdx = [];

            selectedProducers.forEach(item => {
                producers.push(item);
                producersIdx.push(item.guid);
            });

            payload.producers = producers;
            payload.producersIdx = producersIdx;
        }

        // add Cast if valid
        if (selectedCast !== undefined && Array.isArray(selectedCast)) {
            var cast = [];
            var castIdx = [];

            selectedCast.forEach(item => {
                cast.push(item);
                castIdx.push(item.guid);
            });

            payload.cast = cast;
            payload.castIdx = castIdx;
        }

        // add Watch if valid
        if (selectedWatch !== undefined && Array.isArray(selectedWatch)) {
            var watchItems = [];

            selectedWatch.forEach(item => {
                watchItems.push(item);
            });

            payload.watch = watchItems;
        }

        // add Country if valid
        if (form.country !== undefined && form.country.length === 2) {
            payload.country = [form.country];
        }

        // add Language if valid
        if (form.language && form.language.length === 2) {
            payload.language = form.language;
        }

        // add num episodes if valid
        // 2026-08-29 (URGENT -- Chris, blocked live tonight): form.numEpisodes is a
        // raw NUMBER on load (initialValues seeds it straight from the Firestore
        // field, see initialValues above), not a string -- it only becomes a string
        // if the user actually retypes into that specific field. Editing any OTHER
        // field on a kind:2 title (e.g. just fixing the trailer link) left it as a
        // number, and form.numEpisodes.trim() threw (numbers have no .trim()) before
        // this function ever reached updateDoc() or navigate() -- with nothing
        // catching that throw and no setModalSpinnerOpen(false) anywhere in this
        // file, the "Please be patient" modal (only ever closed by navigating away
        // on success) hung forever. Every edit to every series hit this, since none
        // of them require touching numEpisodes to trigger it. Fixed by coercing to a
        // string first, same safe pattern already used elsewhere in this file
        // (String(cur).trim(), further down).
        if (form.numEpisodes !== undefined && String(form.numEpisodes).trim().length > 0) {
            let intVal = parseInt(form.numEpisodes);
            if (!isNaN(intVal)) {
                payload.numEpisodes = intVal;
            }
        } else if (form.kind === 2 || form.kind === "2") {
            // delete the field on the document if the user blanked the form field
            payload.numEpisodes = deleteField();
        }

        // Debug:
        // window.alert(JSON.stringify(payload)); return;

        setModalMessage('Updating title...');
        console.log("Updating doc " + titleId);

        // PART A: save the record
        const titleDocRef = doc(firestore, "titles", titleId);
        await updateDoc(titleDocRef, payload)
            .catch(error => {
                console.log("Error updating doc: " + error);
            });

        // 2026-09-01 (Pascal): manual poster replace on Edit.
        // artworkImgFile is only ever set by handleArtworkImageUpload (i.e. only
        // when the user actually picked a new file) -- undefined here means the
        // poster is untouched, matching Add Title's equivalent PART B.
        //
        // 2026-09-03 (Chris: "old poster is back on the title page" after Update):
        // this originally caught the upload error itself and swallowed it (just a
        // console.log), then carried on to navigate() regardless -- so ANY upload
        // failure looked EXACTLY like a successful save. Removed the inner catch --
        // a real failure now throws and is caught by the outer try/catch in
        // handleSubmit (added in the numEpisodes urgent fix), which shows a real
        // error AND keeps the user on the Edit page to retry.
        //
        // 2026-09-03, second bug on the SAME report: the fix above (and CORS being
        // fixed) made the upload genuinely succeed -- confirmed directly against
        // the GCS object's own metadata (bucket.blob().reload()), NOT via
        // cdn.findflix.com, which turned out to be an unreliable way to check this
        // -- but the fixed path (thumbs/<titleId>.png) meant every replacement
        // reused a URL that domain had already cached, and it caches BY PATH ONLY,
        // ignoring the ?v= query-string cache-buster entirely. So the upload and
        // the Firestore write were both genuinely correct, but every viewer kept
        // seeing the stale cached image regardless. Fixed properly this time: each
        // replacement now uploads to a NEW, never-before-seen path
        // (thumbs/<titleId>-<timestamp>.png), which no cache anywhere could
        // possibly already have -- then deletes the previous poster file
        // afterward (best-effort; a failed cleanup doesn't fail the save) so
        // replacing a poster doesn't leave old versions accumulating in Storage.
        if (artworkImgFile !== undefined) {
            setModalMessage('Uploading cover...');
            const previousThumbnail = title.thumbnail;
            const newPath = `thumbs/${titleId}-${Date.now()}.png`;
            const storageRef = ref(storage, newPath);
            await uploadBytes(storageRef, artworkImgFile);
            console.log('Uploaded cover artwork to', newPath);
            await updateDoc(titleDocRef, { thumbnail: `https://cdn.findflix.com/${newPath}` });

            // Best-effort cleanup of the file this just replaced -- whatever path
            // convention it happened to use (the original thumbs/<titleId>.png from
            // Add Title, or an earlier timestamped Edit replacement), parsed
            // straight from the URL that was actually stored rather than assumed.
            if (previousThumbnail && previousThumbnail.startsWith('https://cdn.findflix.com/thumbs/')) {
                const previousPath = previousThumbnail.replace('https://cdn.findflix.com/', '').split('?')[0];
                if (previousPath !== newPath) {
                    deleteObject(ref(storage, previousPath)).catch((err) => {
                        console.warn('Could not delete previous poster (non-fatal):', err);
                    });
                }
            }
        }

        // pressing save goes back to details rather than main
        navigate(`/app/title/${title.id}`);
    }

    // HELPERS

    // 2026-08-19: Year of Release only collects a 4-digit year -- stored as a Timestamp
    // (still needed for existing sort/range-query code across the client apps) using a
    // mid-year date, not Jan 1. Dates are constructed in the browser's local timezone,
    // then stored as UTC -- Jan 1 is the one day of the year a negative timezone offset
    // can roll into the *previous* year, so someone viewing from a different timezone
    // than whoever entered it could see the wrong year. July 1 at noon has ~6 months of
    // buffer either way, far more than any real timezone spread (max ~26h), so the
    // displayed year is always correct regardless of who's viewing it from where.
    const parseDateString = (str) => {
        const year = Number(str);
        const parsedDate = new Date(year, 6, 1, 12, 0, 0);
        return parsedDate;
    }

    const clearDirectorPressed = async (director, setFieldValue, setFieldTouched) => {
        const directorsClone = cloneDeep(selectedDirectors);
        var removeIndex = directorsClone.map(item => item.guid).indexOf(director.guid);
        (removeIndex >= 0) && directorsClone.splice(removeIndex, 1);

        await setSelectedDirectors(directorsClone);

        // 2026-08-19: Director previously had no dirty-tracking at all (unlike
        // Producer/Cast/Watch below, which already use this shadow-field trick) -- meaning
        // removing a director with nothing else changed left Save disabled. Fixed to match.
        formikRef.current.setFieldValue('directorsShadow', new Date());
        formikRef.current.setFieldTouched('directorsShadow', true);
    }

    const clearWriterPressed = async (writer, setFieldValue, setFieldTouched) => {
        const writersClone = cloneDeep(selectedWriters);
        var removeIndex = writersClone.map(item => item.guid).indexOf(writer.guid);
        (removeIndex >= 0) && writersClone.splice(removeIndex, 1);

        await setSelectedWriters(writersClone);

        formikRef.current.setFieldValue('writersShadow', new Date());
        formikRef.current.setFieldTouched('writersShadow', true);
    }

    const clearCinematographerPressed = async (cinematographer, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedCinematographers);
        var removeIndex = clone.map(item => item.guid).indexOf(cinematographer.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedCinematographers(clone);

        formikRef.current.setFieldValue('cinematographersShadow', new Date());
        formikRef.current.setFieldTouched('cinematographersShadow', true);
    }

    const clearEditorPressed = async (editor, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedEditors);
        var removeIndex = clone.map(item => item.guid).indexOf(editor.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedEditors(clone);

        formikRef.current.setFieldValue('editorsShadow', new Date());
        formikRef.current.setFieldTouched('editorsShadow', true);
    }

    const clearComposerPressed = async (composer, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedComposers);
        var removeIndex = clone.map(item => item.guid).indexOf(composer.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedComposers(clone);

        formikRef.current.setFieldValue('composersShadow', new Date());
        formikRef.current.setFieldTouched('composersShadow', true);
    }

    const clearShowrunnerPressed = async (showrunner, setFieldValue, setFieldTouched) => {
        const showrunnersClone = cloneDeep(selectedShowrunners);
        var removeIndex = showrunnersClone.map(item => item.guid).indexOf(showrunner.guid);
        (removeIndex >= 0) && showrunnersClone.splice(removeIndex, 1);

        await setSelectedShowrunners(showrunnersClone);
    }

    const clearCreatorPressed = async (creator, setFieldValue, setFieldTouched) => {
        const creatorsClone = cloneDeep(selectedCreators);
        var removeIndex = creatorsClone.map(item => item.guid).indexOf(creator.guid);
        (removeIndex >= 0) && creatorsClone.splice(removeIndex, 1);

        await setSelectedCreators(creatorsClone);
    }

    const cancelPressed = () => {
        if (title && title.id) {
            navigate(`/app/title/${title.id}`);
        } else {
            navigate(`/app`);
        }
    }

    // from DirectorAutocomplete
    const handleDirectorChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedDirectors.some(element => {
            if (element.guid === newVal.id) {
                return true;
            }

            return false;
        })) {
            return;
        }

        // keep our own copy outside the form to display to the user
        await setSelectedDirectors([{ guid: newVal.id, name: newVal.name }, ...selectedDirectors]);

        formikRef.current.setFieldValue('directorsShadow', new Date());
        formikRef.current.setFieldTouched('directorsShadow', true);
    }

    // from DirectorAutocomplete -- Writer reuses the same directors lookup/create flow
    const handleWriterChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedWriters.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedWriters([{ guid: newVal.id, name: newVal.name }, ...selectedWriters]);

        formikRef.current.setFieldValue('writersShadow', new Date());
        formikRef.current.setFieldTouched('writersShadow', true);
    }

    const handleCinematographerChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedCinematographers.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedCinematographers([{ guid: newVal.id, name: newVal.name }, ...selectedCinematographers]);

        formikRef.current.setFieldValue('cinematographersShadow', new Date());
        formikRef.current.setFieldTouched('cinematographersShadow', true);
    }

    const handleEditorChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedEditors.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedEditors([{ guid: newVal.id, name: newVal.name }, ...selectedEditors]);

        formikRef.current.setFieldValue('editorsShadow', new Date());
        formikRef.current.setFieldTouched('editorsShadow', true);
    }

    const handleComposerChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedComposers.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedComposers([{ guid: newVal.id, name: newVal.name }, ...selectedComposers]);

        formikRef.current.setFieldValue('composersShadow', new Date());
        formikRef.current.setFieldTouched('composersShadow', true);
    }

    // from DirectorAutocomplete
    const handleShowrunnerChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        // check if already in list
        if (selectedShowrunners.some(element => {
            if (element.guid === newVal.id) {
                return true;
            }

            return false;
        })) {
            return;
        }

        // keep our own copy outside the form to display to the user
        await setSelectedShowrunners([{ guid: newVal.id, name: newVal.name }, ...selectedShowrunners]);
    }

    // from DirectorAutocomplete
    const handleCreatorChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        // check if already in list
        if (selectedCreators.some(element => {
            if (element.guid === newVal.id) {
                return true;
            }

            return false;
        })) {
            return;
        }

        // keep our own copy outside the form to display to the user
        await setSelectedCreators([{ guid: newVal.id, name: newVal.name }, ...selectedCreators]);
    }

    const clearProducerPressed = async (producer, setFieldValue, setFieldTouched) => {
        const producersClone = cloneDeep(selectedProducers);

        var removeIndex = producersClone.map(item => item.guid).indexOf(producer.guid);
        (removeIndex >= 0) && producersClone.splice(removeIndex, 1);

        await setSelectedProducers(producersClone);

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('producersShadow', new Date());
        formikRef.current.setFieldTouched('producersShadow', true);
    }

    const clearCastPressed = async (actor, setFieldValue, setFieldTouched) => {
        const castClone = cloneDeep(selectedCast);

        var removeIndex = castClone.map(item => item.guid).indexOf(actor.guid);
        (removeIndex >= 0) && castClone.splice(removeIndex, 1);

        await setSelectedCast(castClone);

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('castShadow', new Date());
        formikRef.current.setFieldTouched('castShadow', true);
    }

    const clearWatchPressed = async (watchItem, setFieldValue, setFieldTouched) => {
        const watchClone = cloneDeep(selectedWatch);

        var removeIndex = watchClone.map(item => item.url).indexOf(watchItem.url);
        (removeIndex >= 0) && watchClone.splice(removeIndex, 1);

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('watchShadow', new Date());
        formikRef.current.setFieldTouched('watchShadow', true);

        await setSelectedWatch(watchClone);
    }

    const showAddProducer = (e) => {
        e.stopPropagation();

        setModalProducerOpen(true);
    }

    const showAddActor = (e) => {
        e.stopPropagation();

        setModalActorOpen(true);
    }

    const showAddWatch = (e) => {
        e.stopPropagation();

        setModalWatchOpen(true);
    }

    const addProducerHandler = async (item) => {
        setModalProducerOpen(false);

        // validate
        if (!item || !item.guid || !item.name || !item.title) {
            return;
        }

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","title":"Producer"}
        await setSelectedProducers([item, ...selectedProducers]);

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('producersShadow', new Date());
        formikRef.current.setFieldTouched('producersShadow', true);
    }

    const addActorHandler = async (item) => {
        setModalActorOpen(false);

        // validate
        if (!item || !item.guid || !item.name) {
            return;
        }

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","role":"Producer"}
        await setSelectedCast([item, ...selectedCast]);

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('castShadow', new Date());
        formikRef.current.setFieldTouched('castShadow', true);
    }

    const addWatchHandler = async (item) => {
        setModalWatchOpen(false);

        // validate
        if (!item || !item.title || !item.url) {
            return;
        }

        // Force Formik to see the form as Dirty else save button stays greyed out
        formikRef.current.setFieldValue('watchShadow', new Date());
        formikRef.current.setFieldTouched('watchShadow', true);

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","role":"Producer"}
        await setSelectedWatch([item, ...selectedWatch]);
    }

    // 2026-09-01: was missing a reject path entirely (same bug fixed on Add Title
    // in PR #17 -- "resizeFile timed out") -- harmless here only because this whole
    // upload flow was disabled (file input commented out, this function unreachable)
    // until re-enabled today. Now that it's live, a hang here would leave
    // artworkImgFile unset with no error, so Save would silently do nothing to the
    // poster -- fixed with the same 10s timeout + reject path as Add.
    const resizeFile = (file) =>
        new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('resizeFile timed out')), 10000);
            try {
                Resizer.imageFileResizer(file, 200, 300, "PNG", 95, 0,
                    (uri) => {
                        clearTimeout(timeout);
                        resolve(uri);
                    },
                    "file"
                );
            } catch (e) {
                clearTimeout(timeout);
                reject(e);
            }
        });

    const handleArtworkImageUpload = (e) => {
        const [file] = e.target.files;
        if (file) {
            const reader = new FileReader();
            artworkImgRef.current.file = file;
            reader.onload = async (e) => {
                artworkImgRef.current.src = e.target.result;
                const deprefixed = e.target.result.replace(/^(data:image\/png;base64,)/, ''); // data:image/png;base64,
                setArtworkImgEncoded(deprefixed);
                // 2026-09-03 (Chris, reported same day as the poster-replace feature
                // shipped): picking a new poster never touched Formik at all --
                // artworkImgFile is plain React state outside Formik's values, so
                // `dirty` stayed false and Update stayed disabled forever, exactly
                // the same "shadow field" gap every other manually-managed field
                // here (directors, cast, watch, etc.) already has a fix for. Bumping
                // a posterShadow field the same way makes Update enable correctly.
                if (formikRef.current) {
                    formikRef.current.setFieldValue('posterShadow', new Date());
                    formikRef.current.setFieldTouched('posterShadow', true);
                }
                // 2026-09-01: set the fallback (unresized) file immediately, same as
                // Add Title -- so a resize failure/timeout still leaves a real file
                // ready to upload on Save, rather than silently uploading nothing.
                setArtworkImgFile(file);
                try {
                    const resizedImage = await resizeFile(file);
                    setArtworkImgFile(resizedImage);
                } catch (resizeError) {
                    console.warn('[handleArtworkImageUpload] resize failed, uploading original image instead:', resizeError);
                }
            }
            reader.readAsDataURL(file);
        }
    };

    const notRatedChanged = (e, setFieldValue, setFieldTouched) => {
        if (e !== undefined && e.target.checked) {
            setFieldValue('rating', "Not rated");
            setFieldTouched('rating', true);
            ratingFieldRef.current.value = "Not rated";
            ratingFieldRef.current.disabled = true;
        } else {
            setFieldValue('rating', undefined);
            setFieldTouched('rating', true);
            ratingFieldRef.current.value = "";
            ratingFieldRef.current.disabled = false;
        }
    }

    // 2026-08-19: lets a full YouTube/TMDB URL be pasted directly into these fields --
    // extracts just the ID/path and overwrites both the visible input text and Formik's
    // value with it. No-op if what's pasted/typed doesn't look like a URL (see
    // urlParsing.js), so typing a bare ID directly still works exactly as before.
    const handleTrailerFieldChange = (e, setFieldValue) => {
        const extracted = extractYoutubeId(e.target.value);
        e.target.value = extracted;
        setFieldValue('trailer', extracted);
    }

    const handleTmdbFieldChange = (e, setFieldValue) => {
        const extracted = extractTmdbPath(e.target.value);
        e.target.value = extracted;
        setFieldValue('idTMDB', extracted);

        if (/^\/movie\/\d+/.test(extracted)) {
            populateFromTmdbEdit(extracted);
        }
    }

    // 2026-08-19: same URL-paste extraction as the Add page's visible IMDB field.
    const handleImdbFieldChange = (e, setFieldValue) => {
        const extracted = extractImdbId(e.target.value);
        e.target.value = extracted;
        setFieldValue('idIMDB', extracted);
    }

    // 2026-08-19 (Pascal): TMDB re-fetch on Edit. Pasting/typing a valid TMDB movie
    // path fills EMPTY fields and CORRECTS filled fields that differ from TMDB,
    // reporting exactly what it touched in the note under the field. Differences from
    // the Add page's populateFromTmdb:
    //   - never touches the poster (existing artwork stays unless changed manually)
    //   - never touches genres/tags/rating/trailer (Chris-curated)
    //   - "corrects" = TMDB is treated as source of truth for the fields it covers;
    //     every correction is listed in the note so nothing changes silently
    const populateFromTmdbEdit = async (tmdbPath) => {
        setTmdbFetchStatus('loading');
        setTmdbFetchNote(undefined);

        const data = await fetchTmdbMovieDetails(tmdbPath);

        if (data.error) {
            setTmdbFetchStatus('error');
            setTmdbFetchNote(
                data.error === 'no_api_key' ? 'TMDB auto-fill unavailable (no API key in this build).'
                    : data.error === 'not_found' ? 'No TMDB movie found for that link.'
                        : 'Could not reach TMDB -- fill in or correct fields manually.'
            );
            return;
        }

        const fk = formikRef.current;
        const vals = fk.values;
        const filled = [];
        const corrected = [];

        // Scalar helper: fills when current value is empty, corrects when it differs.
        const syncScalar = (field, newVal, ref, label) => {
            if (newVal === undefined || newVal === null) return;
            const cur = vals[field];
            const curEmpty = cur === undefined || cur === null || String(cur).trim() === '';
            if (!curEmpty && String(cur).trim() === String(newVal).trim()) return;
            if (ref && ref.current) ref.current.value = newVal;
            fk.setFieldValue(field, newVal);
            fk.setFieldTouched(field, true);
            (curEmpty ? filled : corrected).push(label);
        };

        syncScalar('titleDisplay', data.tmdbTitle, titleDisplayRef, 'title');
        syncScalar('synopsis', data.overview, synopsisRef, 'synopsis');
        syncScalar('released', data.releaseYear !== undefined ? String(data.releaseYear) : undefined, releasedRef, 'year');
        syncScalar('idIMDB', data.imdbId, idIMDBRef, 'IMDB id');
        syncScalar('country', data.countryCode, undefined, 'country');
        syncScalar('language', data.languageCode, undefined, 'language');

        // Runtime is split across two inputs; compare/update as a pair.
        if (data.runtimeMinutes !== undefined) {
            const hh = Math.floor(data.runtimeMinutes / 60);
            const mm = data.runtimeMinutes % 60;
            const curHH = parseInt(vals.runtimeHH);
            const curMM = parseInt(vals.runtimeMM);
            const curEmpty = isNaN(curHH) && isNaN(curMM);
            if (curEmpty || curHH !== hh || curMM !== mm) {
                if (runtimeHHRef.current) runtimeHHRef.current.value = hh;
                if (runtimeMMRef.current) runtimeMMRef.current.value = mm;
                fk.setFieldValue('runtimeHH', hh);
                fk.setFieldValue('runtimeMM', mm);
                fk.setFieldTouched('runtimeHH', true);
                (curEmpty ? filled : corrected).push('runtime');
            }
        }

        // People-list helper: fills when the current list is empty, replaces it when
        // the TMDB name set differs (order-insensitive compare). extras maps TMDB's
        // per-person metadata onto the created records (producer title, cast role).
        //
        // 2026-09-03 (URGENT -- same root cause as the Add Title stuck-spinner bug,
        // found while fixing that one, see tmdbService.js for the full writeup):
        // this matched tmdbItems[i] to people[i] by ARRAY INDEX, and extras()
        // callers below spread `title`/`role` straight from TMDB data that can be
        // undefined (no character/job credit -- common on documentaries). Either
        // one could put an explicit `undefined` into a field or misalign a person
        // with the wrong role -- Firestore's updateDoc() would reject the whole
        // write if that undefined ever reached a save. Fixed by matching on name
        // instead of index, and only spreading extras() when it actually returns
        // something.
        const syncPeople = async (label, tmdbItems, currentSelected, setSelected, shadowField, collectionName, extras) => {
            if (!tmdbItems || tmdbItems.length === 0) return;
            const tmdbNames = extras ? tmdbItems.map(i => i.name) : tmdbItems;
            const curNames = currentSelected.map(p => p.name);
            const same = curNames.length === tmdbNames.length && tmdbNames.every(n => curNames.includes(n));
            if (same) return;
            const people = await findOrCreatePeople(firestore, auth, collectionName, tmdbNames);
            if (people.length === 0) return;
            const mapped = extras ? people.map(p => {
                const match = tmdbItems.find(i => i.name === p.name);
                const extra = match ? extras(match) : undefined;
                return extra ? { ...p, ...extra } : p;
            }) : people;
            setSelected(mapped);
            fk.setFieldValue(shadowField, new Date());
            fk.setFieldTouched(shadowField, true);
            (curNames.length === 0 ? filled : corrected).push(label);
        };

        // Directors/Writers/Cinematographers/Editors/Composers/Producers share the
        // `directors` people collection; Cast has its own.
        await syncPeople('director(s)', data.directors, selectedDirectors, setSelectedDirectors, 'directorsShadow', 'directors');
        await syncPeople('writer(s)', data.writers, selectedWriters, setSelectedWriters, 'writersShadow', 'directors');
        await syncPeople('cinematographer(s)', data.cinematographers, selectedCinematographers, setSelectedCinematographers, 'cinematographersShadow', 'directors');
        await syncPeople('editor(s)', data.editors, selectedEditors, setSelectedEditors, 'editorsShadow', 'directors');
        await syncPeople('composer(s)', data.composers, selectedComposers, setSelectedComposers, 'composersShadow', 'directors');
        await syncPeople('producer(s)', data.producers, selectedProducers, setSelectedProducers, 'producersShadow', 'directors', (i) => (i.title ? { title: i.title } : undefined));
        await syncPeople('cast', data.cast, selectedCast, setSelectedCast, 'castShadow', 'cast', (i) => (i.role ? { role: i.role } : undefined));

        // Add the TMDB watch-page entry if this title doesn't have one yet -- same
        // entry the Add page auto-creates, closing the gap for pre-2026-08-19 titles
        // one edit at a time (a bulk backfill for the rest is a separate decision).
        if (!selectedWatch.some(w => w.url && w.url.includes('themoviedb.org'))) {
            setSelectedWatch(prev => [
                { title: 'Where to watch (TMDB)', url: `https://www.themoviedb.org${tmdbPath}/watch` },
                ...prev,
            ]);
            fk.setFieldValue('watchShadow', new Date());
            fk.setFieldTouched('watchShadow', true);
            filled.push('where-to-watch link');
        }

        const parts = [];
        if (filled.length > 0) parts.push(`filled: ${filled.join(', ')}`);
        if (corrected.length > 0) parts.push(`corrected: ${corrected.join(', ')}`);
        setTmdbFetchStatus('success');
        setTmdbFetchNote(parts.length > 0
            ? `Checked against TMDB -- ${parts.join('; ')}. Poster, genres, tags, rating and trailer untouched. Review, then Save.`
            : 'Checked against TMDB -- everything already matches. Nothing changed.');
    };

    // 2026-08-19 (Pascal): the where-to-watch fallback above only ran when
    // populateFromTmdbEdit fired, which requires an onChange on the TMDB field --
    // it never fires just from opening a title that already has a TMDB id saved
    // (nothing changes in that field). Bug in practice: remove a stale watch link,
    // Save, reopen -- no re-paste happens, so the TMDB fallback link never got
    // added back. Fixed with a mount-time check: if this title already has a TMDB
    // id and no themoviedb.org watch entry, add the fallback immediately on open.
    // Pure URL construction from the already-stored id, no TMDB API call needed,
    // so this is safe to run unconditionally (unlike the full field re-fetch above,
    // which stays paste-triggered since it can correct Chris's existing data).
    useEffect(() => {
        const tmdbPath = initialValues.idTMDB;
        if (!tmdbPath) return;
        if (selectedWatch.some(w => w.url && w.url.includes('themoviedb.org'))) return;

        setSelectedWatch(prev => [
            { title: 'Where to watch (TMDB)', url: `https://www.themoviedb.org${tmdbPath}/watch` },
            ...prev,
        ]);
        setTmdbFetchStatus('success');
        setTmdbFetchNote('No "Where to watch" link was set -- added the TMDB watch page automatically. Save to keep it.');
        if (formikRef.current) {
            formikRef.current.setFieldValue('watchShadow', new Date());
            formikRef.current.setFieldTouched('watchShadow', true);
        }
        // run once on mount only -- deliberately not reacting to selectedWatch/idTMDB
        // changes after that, which are already covered by the paste-triggered flow
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const countryHandler = async (item, setFieldValue, setFieldTouched) => {
        // window.alert(JSON.stringify(item));

        if (item && item.id && item.id.length === 2) {
            await setFieldValue('country', item.id);
            await setFieldTouched('country', true);
        }
    }

    const languageHandler = async (item, setFieldValue, setFieldTouched) => {
        // window.alert(JSON.stringify(item));

        if (item && item.id && item.id.length === 2) {
            await setFieldValue('language', item.id);
            await setFieldTouched('language', true);
        }
    }

    return (
        <>
            {modalSpinnerOpen && <ModalLoading open={modalSpinnerOpen} message={modalMessage} />}
            {modalProducerOpen && <AddProducerModal isOpen={modalProducerOpen} handler={addProducerHandler} />}
            {modalActorOpen && <AddActorModal isOpen={modalActorOpen} handler={addActorHandler} />}
            {modalWatchOpen && <AddWatchModal isOpen={modalWatchOpen} handler={addWatchHandler} />}

            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} validateOnMount={true} innerRef={formikRef}>
                {({ handleSubmit, handleChange, handleBlur, isValid, dirty, isSubmitting, errors, setFieldValue, setFieldTouched, values }) => (
                    <form onSubmit={handleSubmit} className="">

                        <div className="space-y-12 sm:space-y-16">
                            <div>
                                <div className="sticky top-0 z-30 py-2 bg-white border-b border-gray-300">
                                    <div className='sm:flex sm:items-center sm:justify-between'>
                                        <h1 className="my-2 text-2xl font-bold text-gray-900 sm:text-3xl">
                                            Edit Title: &nbsp;
                                            {title.title}
                                        </h1>
                                        <div className="mt-3 flex sm:mt-0 sm:ml-4">
                                            <button type="button" onClick={cancelPressed} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-isober-500">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="my-2 max-w-2xl text-sm leading-6 text-gray-600">
                                            {debugShowFormErrors && errors && Object.keys(errors).length > 0 && Object.keys(errors).map((key) => (
                                                <span key={key} className="text-orange-500">{key}: {errors[key]} ; &nbsp;</span>
                                            ))}
                                        </p>
                                    </div>
                                </div>

                                {errorMessage && <ErrorBanner message={errorMessage} />}

                                <div className="mt-2 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:pb-0">
                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="title-display" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Display title &nbsp;
                                            <ErrorMessage name="titleDisplay" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="">
                                                <input ref={titleDisplayRef} type="text" name="titleDisplay" id="titleDisplay" autoComplete="off" defaultValue={initialValues.titleDisplay} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Synopsis &nbsp;
                                            <ErrorMessage name="synopsis" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <textarea ref={synopsisRef} id="synopsis" name="synopsis" rows={4} autoComplete="off" defaultValue={initialValues.synopsis} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Director(s) &nbsp;
                                            <ErrorMessage name="director" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedDirectors !== undefined && Array.isArray(selectedDirectors) && selectedDirectors.length > 0) && <div>
                                                {selectedDirectors.map((director, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{director.name}</span>
                                                        <button type="button" onClick={() => clearDirectorPressed(director, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleDirectorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="writers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Writer(s)
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedWriters !== undefined && Array.isArray(selectedWriters) && selectedWriters.length > 0) && <div>
                                                {selectedWriters.map((writer, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{writer.name}</span>
                                                        <button type="button" onClick={() => clearWriterPressed(writer, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleWriterChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="cinematographers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Cinematographer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedCinematographers !== undefined && Array.isArray(selectedCinematographers) && selectedCinematographers.length > 0) && <div>
                                                {selectedCinematographers.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearCinematographerPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleCinematographerChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="editors" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Editor(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedEditors !== undefined && Array.isArray(selectedEditors) && selectedEditors.length > 0) && <div>
                                                {selectedEditors.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearEditorPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleEditorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="composers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Composer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedComposers !== undefined && Array.isArray(selectedComposers) && selectedComposers.length > 0) && <div>
                                                {selectedComposers.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearComposerPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleComposerChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        {(useSeriesMetadata === true) && <>
                                            <label htmlFor="showrunner" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                                Showrunner(s) &nbsp;
                                                <ErrorMessage name="showrunner" component="span" className="text-red-700 italic" />
                                            </label>
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                {(selectedShowrunners !== undefined && Array.isArray(selectedShowrunners) && selectedShowrunners.length > 0) && <div>
                                                    {selectedShowrunners.map((showrunner, index) => (
                                                        <div key={index}>
                                                            <span className='text-green-700 pl-1'>{showrunner.name}</span>
                                                            <button type="button" onClick={() => clearShowrunnerPressed(showrunner, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>}
                                                <DirectorAutocomplete handler={handleShowrunnerChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                            </div>

                                            <label htmlFor="creators" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                                Creator(s) &nbsp;
                                                <ErrorMessage name="creators" component="span" className="text-red-700 italic" />
                                            </label>
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                {(selectedCreators !== undefined && Array.isArray(selectedCreators) && selectedCreators.length > 0) && <div>
                                                    {selectedCreators.map((creator, index) => (
                                                        <div key={index}>
                                                            <span className='text-green-700 pl-1'>{creator.name}</span>
                                                            <button type="button" onClick={() => clearCreatorPressed(creator, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>}
                                                <DirectorAutocomplete handler={handleCreatorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                            </div>

                                            <label htmlFor="rating" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                                Number of episodes <br />
                                                <span className='text-gray-400'>Optional</span> <br />
                                                <ErrorMessage name="numEpisodes" component="span" className="text-red-700 italic" />
                                            </label>
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                    <div className="flex items-center gap-x-3">
                                                        <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                            <input type="text" name="numEpisodes" id="numEpisodes" defaultValue={initialValues.numEpisodes} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>}

                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Producer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedProducers !== undefined && Array.isArray(selectedProducers) && selectedProducers.length > 0) && <div>
                                                {selectedProducers.map((producer, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{producer.title}: {producer.name}</span>
                                                        <button type="button" onClick={() => clearProducerPressed(producer, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddProducer} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add producer
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Cast &nbsp;
                                            <ErrorMessage name="cast" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedCast !== undefined && Array.isArray(selectedCast) && selectedCast.length > 0) && <div>
                                                {selectedCast.map((actor, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{actor.name} {actor.role && <span>: {actor.role}</span>}</span>
                                                        <button type="button" onClick={() => clearCastPressed(actor, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddActor} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add actor
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Where to watch  <br />
                                            <span className='text-gray-400'>Optional</span> <br />
                                            <ErrorMessage name="watch" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedWatch !== undefined && Array.isArray(selectedWatch) && selectedWatch.length > 0) && <div>
                                                {selectedWatch.map((watchItem, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{watchItem.title} at: <u>{watchItem.url}</u></span>
                                                        <button type="button" onClick={() => clearWatchPressed(watchItem, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddWatch} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add item
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Kind &nbsp;
                                            <ErrorMessage name="kind" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <input id="kind-movie" name="kind" value="1" onClick={() => { setUseSeriesMetadata(false); }} defaultChecked={initialValues.kind === "1" || initialValues.kind === 1} type="radio" onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="kind-movie" className="block text-sm leading-6 text-gray-900">
                                                    Movie
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-x-3">
                                                <input id="kind-series" name="kind" value="2" onClick={() => { setUseSeriesMetadata(true); }} defaultChecked={initialValues.kind === "2" || initialValues.kind === 2} type="radio" onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="kind-series" className="block text-sm leading-6 text-gray-900">
                                                    Series
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Display on &nbsp;
                                            <ErrorMessage name="channelBlackphlix" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <input id="channel-blackphlix" name="channelBlackphlix" type="checkbox" defaultChecked={initialValues.channelBlackphlix} onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="channel-blackphlix" className="block text-sm leading-6 text-gray-900">
                                                    BlackPhlix
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-x-3">
                                                <input id="channel-findflix" name="channelFindflix" type="checkbox" defaultChecked={initialValues.channelFindflix} onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="channel-findflix" className="block text-sm leading-6 text-gray-900">
                                                    FindFlix
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="released" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Year of Release &nbsp;
                                            <ErrorMessage name="released" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                <input ref={releasedRef} type="text" name="released" id="released" defaultValue={initialValues.released} autoComplete="none" maxLength={4} inputMode="numeric" onChange={handleChange} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="YYYY" />
                                            </div>
                                        </div>

                                        <label htmlFor="runtime" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Runtime &nbsp;
                                            <ErrorMessage name="runtimeHH" component="span" className="text-red-700 italic" /> &nbsp;
                                            <ErrorMessage name="runtimeMM" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="flex items-center gap-x-3">

                                                    <div className="relative mt-2 rounded-md shadow-sm">
                                                        <input ref={runtimeHHRef} type="text" name="runtimeHH" id="runtimeHH" defaultValue={initialValues.runtimeHH} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 pl-2 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="0" />
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <span className="text-gray-500 sm:text-sm" id="price-currency">
                                                                Hours
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="relative mt-2 rounded-md shadow-sm">
                                                        <input ref={runtimeMMRef} type="text" name="runtimeMM" id="runtimeMM" defaultValue={initialValues.runtimeMM} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 pl-2 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="0" />
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <span className="text-gray-500 sm:text-sm" id="price-currency">
                                                                Minutes
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <label htmlFor="rating" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Rating &nbsp;
                                            <ErrorMessage name="rating" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="flex items-center gap-x-3">
                                                    <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                        <input type="text" name="rating" id="rating" ref={ratingFieldRef} defaultValue={initialValues.rating} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="PG13" />
                                                    </div>
                                                    <div>
                                                        <input type="checkbox" id="ratingNotRated" onChange={(e) => { notRatedChanged(e, setFieldValue, setFieldTouched) }} className='mx-2 focus:ring-isober-500 h-4 w-4 text-isober-600 border-gray-300 rounded' />
                                                        <label htmlFor="ratingNotRated" className='text-sm font-medium text-gray-700'>Not rated</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Is featured
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0 flex flex-row">
                                            <div className="mr-2 flex items-center gap-x-2">
                                                <input id="featured-yes" name="featured" value="1" type="radio" defaultChecked={initialValues.featured === true} onChange={handleChange} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="featured-yes" className="block text-sm leading-6 text-gray-900">
                                                    Yes
                                                </label>
                                            </div>
                                            <div className="ml-2 flex items-center gap-x-2">
                                                <input id="featured-no" name="featured" value="0" type="radio" defaultChecked={initialValues.featured !== true} onChange={handleChange} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="featured-no" className="block text-sm leading-6 text-gray-900">
                                                    No
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Country of origin
                                            <br /><span className='text-gray-400'>Optional</span>
                                            <ErrorMessage name="origin" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {/* 2026-08-19: initialValue wired so the stored value AND any
                                                TMDB correction visibly display (same as the Add page) */}
                                            <CountryChooser initialValue={values.country} handler={countryHandler} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                                        </div>

                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Original language
                                            <br /><span className='text-gray-400'>Optional</span>
                                            <ErrorMessage name="language" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <LanguageChooser initialValue={values.language} handler={languageHandler} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Youtube trailer ID &nbsp;
                                            <ErrorMessage name="trailer" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                <input type="text" name="trailer" id="trailer" defaultValue={initialValues.trailer} autoComplete="none" onChange={(e) => handleTrailerFieldChange(e, setFieldValue)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="Youtube ID or full URL" />
                                            </div>
                                        </div>

                                        <label htmlFor="idTMDB" className="block text-sm font-medium leading-6 text-gray-900">
                                            Movie Database ID <br />
                                            <span className='text-gray-400'>Paste a TMDB URL to fill empty fields and correct existing ones</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                    <input type="text" name="idTMDB" id="idTMDB" defaultValue={initialValues.idTMDB} autoComplete="none" onChange={(e) => handleTmdbFieldChange(e, setFieldValue)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="/movie/184-jackie-brown or full URL" />
                                                </div>
                                                <img src="/images/Partner_TMDB.png" alt="" />
                                                <ErrorMessage name="idTMDB" component="span" className="text-red-700 italic" />
                                            </div>
                                            {tmdbFetchStatus === 'loading' && (
                                                <p className="mt-1 text-sm text-gray-500">Checking against TMDB...</p>
                                            )}
                                            {tmdbFetchStatus === 'success' && (
                                                <p className="mt-1 text-sm text-green-700">{tmdbFetchNote}</p>
                                            )}
                                            {tmdbFetchStatus === 'error' && (
                                                <p className="mt-1 text-sm text-red-700">{tmdbFetchNote}</p>
                                            )}
                                        </div>

                                        <label htmlFor="idIMDB" className="block text-sm font-medium leading-6 text-gray-900">
                                            IMDB ID <br />
                                            <span className='text-gray-400'>Fills/corrects from TMDB -- paste an IMDB URL to set it manually</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                    <input ref={idIMDBRef} type="text" name="idIMDB" id="idIMDB" defaultValue={initialValues.idIMDB} autoComplete="none" onChange={(e) => handleImdbFieldChange(e, setFieldValue)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="tt1234567 or full URL" />
                                                </div>
                                                <ErrorMessage name="idIMDB" component="span" className="text-red-700 italic" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Genres
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <Field name="validdevices" component={GenreChooser} initialSelection={initialValues.genres} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Tags
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <Field name="validdevices" component={TagChooser} initialSelection={initialValues.tags} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Poster
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {/* 2026-09-01 (Pascal): re-enabled -- was disabled pending "correctly
                                                implement deleting the old one". Originally assumed a fixed path
                                                (overwrite in place) made that unnecessary, but that broke in
                                                practice: cdn.findflix.com caches by path only, ignoring any
                                                cache-busting query string, so a same-path overwrite kept serving
                                                the stale cached image to every viewer indefinitely (found
                                                2026-09-03). Now DOES delete the old file -- each replacement
                                                uploads to a fresh, never-cached path, then removes the previous
                                                one. See handleSubmitInner. */}
                                            <input id="file" name="file" type="file" accept="image/png, image/jpeg" onChange={handleArtworkImageUpload} />
                                            {artworkImgEncoded === undefined && title.thumbnail !== undefined && <img src={title.thumbnail} alt="" className='mt-2 w-40 h-60 object-cover' />}
                                            {artworkImgEncoded === undefined && title.thumbnail === undefined && <p className='mt-2 text-sm text-gray-500'>Not set</p>}
                                            <div className={artworkImgEncoded === undefined ? 'hidden' : 'mt-2'}>
                                                <p className='text-xs text-gray-500 mb-1'>New poster (not saved yet):</p>
                                                <img ref={artworkImgRef} className='w-40 h-60 object-cover border-none border-0' alt="" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Button actions */}
                        <div className="mt-4 flex justify-end">
                            <button type="submit" disabled={isSubmitting || !dirty || !isValid} className="disabled:opacity-60 bg-indigo-800 hover:bg-indigo-900 py-2 px-4 border border-indigo-900 rounded-md shadow-sm text-sm font-medium text-white disabled:text-gray-2    00 hover:bg-indigo-1000 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                Update title
                            </button>
                        </div>
                    </form>
                )}
            </Formik>
        </>
    );
}